import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import axios from 'axios'
import { Button, Row, Col, Form, Table, Badge } from 'react-bootstrap'
import ModalContainer from './ModalContainer'
import Loader from './Loader'
import Message from './Message'

import BACKEND_URL from '../../backendUrl'

import { assignParticipant } from '../actions/participantActions'
import { ASSIGN_PARTI_RESET } from '../constants/participantConstants'

const getOptionalIdsForOwnerGroup = (enrol, groupId) => {
  if (!enrol?.optional_tx_assignments_by_group || !groupId) return []
  const row = enrol.optional_tx_assignments_by_group.find(
    (r) => String(r.participant_group?._id || r.participant_group) === String(groupId)
  )
  if (!row?.optional_tx_package_list) return []
  return row.optional_tx_package_list.map((p) => p._id || p)
}

export const AssignParticipant = ({
  enrolment,
  email,
  settingsSetId,
  onSaveSuccess,
}) => {
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo, logout } = userLogin

  const assignParti = useSelector((state) => state.assignParti)
  const {
    loading: loadingAssignParti,
    error: errorAssignParti,
    success: successAssignParti,
  } = assignParti

  // Load the study tree into LOCAL state (not the shared studyDetails redux slice) so
  // opening/closing this modal never clobbers the participant-list filters, which read
  // the same slice. Scoped to the enrolment's settings set (the list is already
  // filtered by that set, so the selected settingsSetId matches the enrolment's set).
  const [loadedStudy, setLoadedStudy] = useState(null)
  const [loadingInitiateStudy, setLoadingInitiateStudy] = useState(false)
  const [errorInitiateStudy, setErrorInitiateStudy] = useState('')
  const successInitiateStudy = !!loadedStudy

  const dispatch = useDispatch()

  const [show, setShow] = useState(false)
  const [currentStage, setCurrentStage] = useState('')
  const [currentGroup, setCurrentGroup] = useState('')

  const [assignStage, setAssignStage] = useState('')
  const [assignGroup, setAssignGroup] = useState('')

  const [saveError, setSaveError] = useState('')
  const [availableOptionalTxPackages, setAvailableOptionalTxPackages] = useState([])
  const [assignedOptionalTxPackages, setAssignedOptionalTxPackages] = useState([])

  useEffect(() => {
    let now = new Date()
    if (userInfo && userInfo.expires > now && !logout) {
      if (successAssignParti) {
        dispatch({ type: ASSIGN_PARTI_RESET })
        setShow(false)
        onSaveSuccess()
      }
    }
  }, [
    enrolment,
    userInfo,
    logout,
    dispatch,
    onSaveSuccess,
    successAssignParti,
  ])

  const handleOpen = async () => {
    let now = new Date()
    if (userInfo && userInfo.expires > now && !logout) {
      if (enrolment) {
        setCurrentStage(enrolment.current_stage.chin_name)
        setCurrentGroup(enrolment.current_group.name)
        setAssignedOptionalTxPackages([])
        // Fetch the study tree scoped to the enrolment's settings set into local state.
        try {
          setErrorInitiateStudy('')
          setLoadingInitiateStudy(true)
          const query = settingsSetId ? `?settingsSetId=${settingsSetId}` : ''
          const config = {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${userInfo.token}`,
            },
          }
          const { data } = await axios.get(
            BACKEND_URL + `/api/studies/${enrolment.study}${query}`,
            config
          )
          setLoadedStudy(data)
        } catch (error) {
          setErrorInitiateStudy(
            error.response && error.response.data.message
              ? error.response.data.message
              : error.message
          )
        } finally {
          setLoadingInitiateStudy(false)
        }
      }
    }
    setShow(true)
  }

  const onChangeStage = (e) => {
    setAssignStage(e.target.value)
    setAssignGroup('')
    setAvailableOptionalTxPackages([])
    setAssignedOptionalTxPackages([])
  }

  useEffect(() => {
    if (assignGroup && loadedStudy) {
      const selectedGroup = loadedStudy.stage_list
        .flatMap((stage) => stage.participant_group_list)
        .find((group) => group._id === assignGroup)

      let optionalPackages = []

      if (selectedGroup && selectedGroup.tx_package_list) {
        optionalPackages = selectedGroup.tx_package_list
          .filter((pkg) => pkg.is_optional === true)
          .map((pkg) => ({
            _id: pkg._id,
            title: pkg.title,
            groupId: assignGroup,
            groupName: selectedGroup.name,
          }))
      }

      const uniquePackages = optionalPackages.filter((pkg, index, self) =>
        index === self.findIndex((p) => p._id === pkg._id)
      )
      const availablePackages = uniquePackages.filter(
        (pkg) => !assignedOptionalTxPackages.includes(pkg._id)
      )

      setAvailableOptionalTxPackages(availablePackages)
    } else {
      setAvailableOptionalTxPackages([])
    }
  }, [assignGroup, loadedStudy, assignedOptionalTxPackages])

  const checkAssignedStageNonEmpty = () => {
    if (assignStage.length === 0) {
      return 'Assigned stage cannot be empty'
    } else {
      return ''
    }
  }

  const checkAssignedGroupNonEmpty = () => {
    if (assignGroup.length === 0) {
      return 'Assigned group cannot be empty'
    } else {
      return ''
    }
  }

  const handleSave = (e) => {
    e.preventDefault()

    setSaveError('')
    let error = ''

    if (error.length === 0) error = checkAssignedStageNonEmpty()
    if (error.length === 0) error = checkAssignedGroupNonEmpty()

    if (error.length === 0) {
      dispatch(
        assignParticipant(enrolment._id, {
          studyId: loadedStudy._id,
          stageId: assignStage,
          groupId: assignGroup,
          optional_tx_packages_for_assigned_group: assignedOptionalTxPackages,
        })
      )
    } else {
      setSaveError(error)
    }
  }

  const handleAddOptionalTxPackage = (pkgId) => {
    if (!assignedOptionalTxPackages.includes(pkgId)) {
      setAssignedOptionalTxPackages([...assignedOptionalTxPackages, pkgId])
    }
  }

  const handleRemoveOptionalTxPackage = (pkgId) => {
    setAssignedOptionalTxPackages(
      assignedOptionalTxPackages.filter((id) => id !== pkgId)
    )
  }

  const handleClose = () => {
    // Local-only cleanup; the shared studyDetails slice is intentionally untouched so
    // the participant-list filters keep their loaded settings set.
    setLoadedStudy(null)
    setErrorInitiateStudy('')
    setAssignStage('')
    setAssignGroup('')
    setShow(false)
    setAvailableOptionalTxPackages([])
    setAssignedOptionalTxPackages([])
  }

  const filteredGroups = (study, stageId) => {
    const targetStage = study.stage_list.find((x) => x._id === stageId)
    return targetStage.participant_group_list
  }

  const selectedGroupMeta =
    assignGroup && loadedStudy
      ? loadedStudy.stage_list
          .flatMap((stage) => stage.participant_group_list)
          .find((g) => g._id === assignGroup)
      : null

  return (
    <>
      <Button variant='primary' className='btn-sm m-1' onClick={handleOpen}>
        Assign To Another Stage
      </Button>

      <ModalContainer
        title='Assign Participant'
        show={show}
        handleSave={handleSave}
        handleClose={handleClose}
      >
        {errorInitiateStudy && (
          <Message variant='danger'>{errorInitiateStudy}</Message>
        )}
        {errorAssignParti && (
          <Message variant='danger'>{errorAssignParti}</Message>
        )}
        {saveError && <Message variant='danger'>{saveError}</Message>}

        {loadingInitiateStudy || loadingAssignParti ? (
          <Loader />
        ) : (
          <>
            {successInitiateStudy && (
              <>
                <Row>
                  <Col md={12}>
                    <Table
                      bordered
                      hover
                      responsive
                      className='table-sm'
                      style={{ marginTop: '20px' }}
                    >
                      <thead>
                        <tr>
                          <th>PARTICIPANT</th>
                          <th>CURRENT STAGE</th>
                          <th>CURRENT GROUP</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>{email}</td>
                          <td>{currentStage}</td>
                          <td>{currentGroup}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group controlId={'assigned_study'}>
                      <Form.Label>Assigned Stage</Form.Label>
                      <Form.Control
                        as='select'
                        value={assignStage}
                        onChange={(e) => {
                          onChangeStage(e)
                        }}
                      >
                        <option value='' key={'empty_stage'}>
                          Please Select
                        </option>
                        {loadedStudy.stage_list.map((stage) => (
                          <option value={stage._id} key={stage._id}>
                            {stage.chin_name}
                          </option>
                        ))}
                      </Form.Control>{' '}
                    </Form.Group>
                  </Col>
                </Row>

                {assignStage !== '' && (
                  <>
                    <Row>
                      <Col>
                        <Form.Group controlId={'assigned_group'}>
                          <Form.Label>Assigned Group</Form.Label>
                          <Form.Control
                            as='select'
                            value={assignGroup}
                            onChange={(e) => {
                              const gid = e.target.value
                              setAssignGroup(gid)
                              setAssignedOptionalTxPackages(
                                getOptionalIdsForOwnerGroup(enrolment, gid)
                              )
                            }}
                          >
                            <option value='' key={'empty_group'}>
                              Please Select
                            </option>
                            {filteredGroups(loadedStudy, assignStage).map(
                              (group) => (
                                <option value={group._id} key={group._id}>
                                  {group.name}
                                </option>
                              )
                            )}
                          </Form.Control>{' '}
                        </Form.Group>
                      </Col>
                    </Row>

                    {assignGroup !== '' && (
                      <>
                        <Row style={{ marginTop: '20px' }}>
                          <Col>
                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                              <Form.Label style={{ marginBottom: 0 }}>
                                Optional treatment chapters for
                              </Form.Label>
                              {selectedGroupMeta && (
                                <Badge variant='info'>{selectedGroupMeta.name}</Badge>
                              )}
                            </div>
                            <div
                              style={{
                                marginTop: '4px',
                                fontSize: '0.8rem',
                                color: '#666',
                              }}
                            >
                              Optional modules listed here belong to the assigned participant group above.
                              Already-assigned chapters for this group are omitted from the Add list.
                            </div>

                            {availableOptionalTxPackages.length > 0 ? (
                              <Table
                                bordered
                                hover
                                responsive
                                className='table-sm'
                                style={{ marginTop: '10px' }}
                              >
                                <thead>
                                  <tr>
                                    <th>Title</th>
                                    <th>Group</th>
                                    <th style={{ width: '100px' }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {availableOptionalTxPackages.map((pkg) => (
                                    <tr key={pkg._id}>
                                      <td>{pkg.title}</td>
                                      <td>{pkg.groupName}</td>
                                      <td>
                                        <Button
                                          variant='success'
                                          size='sm'
                                          onClick={() =>
                                            handleAddOptionalTxPackage(pkg._id)
                                          }
                                        >
                                          Add
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            ) : (
                              <p style={{ marginTop: '10px', fontStyle: 'italic' }}>
                                No optional treatment chapters available for assignment from this group
                                (or all are already selected below).
                              </p>
                            )}
                          </Col>
                        </Row>

                        <Row style={{ marginTop: '20px' }}>
                          <Col>
                            <Form.Label>Optional Treatment Chapters After Assignment</Form.Label>
                            <div
                              style={{
                                marginTop: '2px',
                                marginBottom: '8px',
                                fontSize: '0.8rem',
                                color: '#666',
                              }}
                            >
                              These optional chapters will be saved for this participant group only.
                              Assignments for other groups are unchanged.
                            </div>
                            {assignedOptionalTxPackages.length > 0 ? (
                              <Table
                                bordered
                                hover
                                responsive
                                className='table-sm'
                              >
                                <thead>
                                  <tr>
                                    <th>Title</th>
                                    <th style={{ width: '100px' }}>Action</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {assignedOptionalTxPackages.map((pkgId) => {
                                    let pkg = availableOptionalTxPackages.find((p) => p._id === pkgId)

                                    if (!pkg && loadedStudy) {
                                      const allGroups = loadedStudy.stage_list.flatMap(
                                        (stage) => stage.participant_group_list
                                      )

                                      for (const group of allGroups) {
                                        if (group.tx_package_list) {
                                          const foundPkg = group.tx_package_list.find(
                                            (p) =>
                                              (p._id === pkgId ||
                                                String(p._id) === String(pkgId)) &&
                                              p.is_optional
                                          )
                                          if (foundPkg) {
                                            pkg = {
                                              _id: foundPkg._id,
                                              title: foundPkg.title,
                                              groupName: group.name,
                                            }
                                            break
                                          }
                                        }
                                      }
                                    }

                                    return pkg ? (
                                      <tr key={pkgId}>
                                        <td>{pkg.title}</td>
                                        <td>
                                          <Button
                                            variant='danger'
                                            size='sm'
                                            onClick={() => handleRemoveOptionalTxPackage(pkgId)}
                                          >
                                            Delete
                                          </Button>
                                        </td>
                                      </tr>
                                    ) : (
                                      <tr key={pkgId}>
                                        <td>Package ID: {pkgId}</td>
                                        <td>
                                          <Button
                                            variant='danger'
                                            size='sm'
                                            onClick={() => handleRemoveOptionalTxPackage(pkgId)}
                                          >
                                            Delete
                                          </Button>
                                        </td>
                                      </tr>
                                    )
                                  })}
                                </tbody>
                              </Table>
                            ) : (
                              <p style={{ fontStyle: 'italic' }}>
                                No optional treatment chapters will be assigned for this group
                              </p>
                            )}
                          </Col>
                        </Row>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </ModalContainer>
    </>
  )
}

export default AssignParticipant
